import { useState, useEffect, useCallback, useMemo } from 'react'
import { Form, Field, FormElement, FieldWrapper } from '@progress/kendo-react-form'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout'
import { Switch } from '@progress/kendo-react-inputs'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Label } from '@progress/kendo-react-labels'
import {
  FormNumericTextBox,
  FormInput,
  FormDropDownList,
  FormDropDownListFilter,
  FormDatePicker,
} from '../FormComponents'
import { FaTimesCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa'

import { useToast } from '../../../contexts/ToastContext'
import { dropDown } from '../../../data/interfaces'
import { getPieceById, updatePiece, getPieceFileUrl, createXeroPurchaseInvoice, deletePiece } from '../../../data/piecesData'
import { getJobsForDropdown, getTargetJob } from '../../../data/jobData'
import { getWorktypeDropdown } from '../../../data/worktypeData'
import { 
  getXeroDepartmentsDropdown,
  getXeroAccountsDropdown,
  getXeroTaxRatesDropdown,
  getXeroTypesDropdown,
  getXeroSuppliersDropdown
} from '../../../data/xeroData'
import PdfViewer from '../../PdfViewer/PdfViewer'
import PiecesLineitemManager from './PiecesLineitemManager'

import '../../../pages/Styles.css'
import PiecePurchaseOrderPanel from './PiecePurchaseOrderPanel'

interface EditPieceFormProps {
  lockEdit?: boolean
  onPieceId: number
  onPieceUpdated?: () => void
  onClose?: () => void
  onNavigateToPiece?: (pieceId: number) => void
  piecesList?: number[]
  currentPieceIndex?: number
}

type PieceFormState = {
  piecesId: number
  jobId: number
  projectName: string
  siteName: string
  supplierName: string
  supplierAddress: string
  total: string
  subTotal: string
  totalTax: string
  invoiceId: string
  invoiceDate: string
  dueDate: string
  worktypeId: number
  documentTypeId: number
  description: string
  comments: string
  flagged: boolean
  xeroTrackingDepartmentId: number
  xeroAccountId: number
  xeroTaxRateId: number
  xeroTrackingTypeId: number
  xeroSupplierId: number
  purchaseOrder: string
  submitToXero: boolean
  powerAutomateProcessed: number
}

// Formats a Date as YYYY-MM-DD using its local parts. toISOString() would
// convert to UTC first, which pushes a British Summer Time date back a day.
const toApiDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Supplier payment terms are 30 days end of month: the last day of the month the
// invoice falls in, plus 30 days. An invoice dated 6 Oct 2025 is due 30 Nov 2025.
// Mirrors PiecesModel.CalculateDueDate on the API, which fills this in on create.
const calculateDueDate = (invoiceDate: Date): Date => {
  const endOfMonth = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth() + 1, 0)
  endOfMonth.setDate(endOfMonth.getDate() + 30)
  return endOfMonth
}

// Maps a raw piece API response (PiecesVM) into the form's local state shape.
// Used both on initial load and after a Xero send, so neither path drops the
// raw DTO straight into formState (whose shape differs from the API response).
const mapPieceDataToFormState = (pieceData: any, siteName: string): PieceFormState => ({
  piecesId: pieceData.piecesId,
  jobId: Number(pieceData.jobId || pieceData.job_id) || 0,
  projectName: pieceData.projectName || '',
  siteName,
  supplierName: pieceData.supplierName || '',
  supplierAddress: pieceData.supplierAddress || '',
  total: pieceData.total || '0',
  subTotal: pieceData.subTotal || '0',
  totalTax: pieceData.totalTax || '0',
  invoiceId: pieceData.invoiceId || '',
  invoiceDate: pieceData.invoiceDate || '',
  dueDate: pieceData.dueDate || '',
  worktypeId: pieceData.worktypeId || 0,
  documentTypeId: pieceData.documentTypeId || 1,
  description: pieceData.description || 'Materials',
  comments: pieceData.comments || '',
  flagged: pieceData.flagged || false,
  xeroTrackingDepartmentId: Number(pieceData.xeroTrackingDepartmentId) || 0,
  xeroAccountId: Number(pieceData.xeroAccountId) || 0,
  xeroTaxRateId: Number(pieceData.xeroTaxRateId) || 0,
  xeroTrackingTypeId: Number(pieceData.xeroTrackingTypeId) || 0,
  xeroSupplierId: Number(pieceData.xeroSupplierId || pieceData.xero_supplier_id) || 0,
  purchaseOrder: pieceData.purchaseOrder || '',
  submitToXero: pieceData.submitToXero || false,
  powerAutomateProcessed: Number(pieceData.powerAutomateProcessed) || 0,
})

const EditPieceForm = (props: EditPieceFormProps) => {
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false)
  const [buttonSpinner, setButtonSpinner] = useState(false)
  // Bumped after a save so the purchase order panel rechecks the reference.
  const [purchaseOrderRefreshToken, setPurchaseOrderRefreshToken] = useState(0)
  const [fileUrl, setFileUrl] = useState<string>('')
  const [loadingFile, setLoadingFile] = useState(false)
  const pieceId = props.onPieceId
  
  // Helper function to sanitize invoiceId by replacing carriage returns and newlines with commas
  const sanitizeInvoiceId = (invoiceId: string | null | undefined): string => {
    if (!invoiceId) return '';
    // Replace carriage returns (\r), newlines (\n), and combinations with commas
    return invoiceId.replace(/[\r\n]+/g, ',').trim();
  };
  
  // Helper function to safely convert string to Date
  const parseDate = (dateString: string | null | undefined): Date | null => {
    if (!dateString || dateString === '') return null;
    console.log('parseDate input:', dateString, 'type:', typeof dateString);
    try {
      // First try standard Date parsing
      let date = new Date(dateString);
      
      // If standard parsing fails, try to handle custom format like "Oct  6 2025 11:00PM"
      if (isNaN(date.getTime())) {
        // Replace double spaces with single space and try to normalize the format
        const normalized = dateString.replace(/\s+/g, ' ').trim();
        
        // Try parsing the normalized string
        date = new Date(normalized);
        
        // If still invalid, try to manually parse "Oct 6 2025 11:00PM" format
        if (isNaN(date.getTime())) {
          // Extract just the date part (ignore time for date picker)
          const dateOnly = normalized.split(' ').slice(0, 3).join(' '); // "Oct 6 2025"
          date = new Date(dateOnly);
        }
      }
      
      console.log('parseDate output:', date, 'isValid:', !isNaN(date.getTime()));
      // Check if date is valid
      if (isNaN(date.getTime())) return null;
      return date;
    } catch (error) {
      console.error('parseDate error:', error);
      return null;
    }
  };
  
  // Dropdown data states
  const [jobsData, setJobsData] = useState<dropDown[]>([])
  const [worktypeData, setWorktypeData] = useState<dropDown[]>([])
  const [xeroDepartmentData, setXeroDepartmentData] = useState<dropDown[]>([])
  const [xeroAccountData, setXeroAccountData] = useState<dropDown[]>([])
  const [xeroTaxRateData, setXeroTaxRateData] = useState<dropDown[]>([])
  const [xeroTypeData, setXeroTypeData] = useState<dropDown[]>([])
  const [xeroSupplierData, setXeroSupplierData] = useState<dropDown[]>([])

  // Tab state
  const [activeTab, setActiveTab] = useState(0)
  
  // Track form jobId changes for site name fetching
  const [formJobId, setFormJobId] = useState<number>(0)

  // Document type dropdown options
  const documentTypes = [
    { text: 'Invoice', value: 1 },
    { text: 'Credit', value: 2 }
  ]

  const [formState, setFormState] = useState<PieceFormState>({
    piecesId: 0,
    jobId: 0,
    projectName: '',
    siteName: '',
    supplierName: '',
    supplierAddress: '',
    total: '0',
    subTotal: '0',
    totalTax: '0',
    invoiceId: '',
    invoiceDate: '',
    dueDate: '',
    worktypeId: 0,
    documentTypeId: 1,
    description: 'Materials',
    comments: '',
    flagged: false,
    xeroTrackingDepartmentId: 0,
    xeroAccountId: 0,
    xeroTaxRateId: 0,
    xeroTrackingTypeId: 0,
    xeroSupplierId: 0,
    purchaseOrder: '',
    submitToXero: false,
    powerAutomateProcessed: 0,
  })

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Load required dropdowns first
        const [jobs, worktypes] = await Promise.all([
          getJobsForDropdown(),
          getWorktypeDropdown()
        ])
        
        setJobsData(jobs)
        setWorktypeData(worktypes)

        // Load Xero dropdowns separately and handle failures gracefully
        try {
          const xeroDepartments = await getXeroDepartmentsDropdown()
          setXeroDepartmentData(xeroDepartments)
        } catch (error) {
          console.warn('Xero Departments endpoint not available:', error)
          setXeroDepartmentData([])
        }

        try {
          const xeroAccounts = await getXeroAccountsDropdown()
          setXeroAccountData(xeroAccounts)
        } catch (error) {
          console.warn('Xero Accounts endpoint not available:', error)
          setXeroAccountData([])
        }

        try {
          const xeroTaxRates = await getXeroTaxRatesDropdown()
          setXeroTaxRateData(xeroTaxRates)
        } catch (error) {
          console.warn('Xero Tax Rates endpoint not available:', error)
          setXeroTaxRateData([])
        }

        try {
          const xeroTypes = await getXeroTypesDropdown()
          setXeroTypeData(xeroTypes)
        } catch (error) {
          console.warn('Xero Types endpoint not available:', error)
          setXeroTypeData([])
        }

        try {
          const xeroSuppliers = await getXeroSuppliersDropdown()
          setXeroSupplierData(xeroSuppliers)
        } catch (error) {
          console.warn('Xero Suppliers endpoint not available:', error)
          setXeroSupplierData([])
        }

      } catch (error) {
        console.error('Error fetching required dropdown data:', error)
        showToast('Error loading dropdown data', 'error')
      } finally {
        // Mark dropdowns as loaded regardless of success/failure
        console.log('Debug: Setting dropdownsLoaded to true')
        setDropdownsLoaded(true)
      }
    }

    fetchDropdownData()
  }, [])

  // Fetch piece data when component loads
  useEffect(() => {
    const fetchPieceData = async () => {
      if (!pieceId) return
      
      try {
        setLoading(true)
        console.log('Fetching piece data for ID:', pieceId)
        const pieceData = await getPieceById(pieceId)
        console.log('Received piece data:', pieceData)
        console.log('Invoice date from API:', pieceData.invoiceDate, 'type:', typeof pieceData.invoiceDate)
        const loadedJobId = Number(pieceData.jobId || pieceData.job_id) || 0
        setFormState(mapPieceDataToFormState(pieceData, ''))
        
        // Update formJobId to sync with loaded piece data
        setFormJobId(loadedJobId)

        // Fetch site name if job is selected
        if (pieceData.jobId) {
          try {
            const jobDetails = await getTargetJob(pieceData.jobId)
            setFormState(prevState => ({
              ...prevState,
              siteName: jobDetails.site?.siteAddr || ''
            }))
          } catch (error) {
            console.error('Error fetching job site details:', error)
          }
        }
      } catch (error) {
        console.error('Error fetching piece data:', error)
        showToast('Error loading piece data', 'error')
        setLoading(false) // Make sure loading is set to false even on error
      } finally {
        setLoading(false)
      }
    }

    console.log('useEffect triggered - pieceId:', pieceId, 'jobsData.length:', jobsData.length, 'dropdownsLoaded:', dropdownsLoaded)
    if (jobsData.length > 0 && dropdownsLoaded) { // Only fetch piece data after ALL dropdowns are loaded
      fetchPieceData()
    }
  }, [pieceId, jobsData.length, dropdownsLoaded])

  // Fetch file URL when piece ID changes
  useEffect(() => {
    const fetchFileUrl = async () => {
      if (!pieceId) {
        setFileUrl('')
        return
      }
      
      try {
        setLoadingFile(true)
        const url = await getPieceFileUrl(pieceId)
        setFileUrl(url)
      } catch (error) {
        console.warn('File URL endpoint not available:', error)
        setFileUrl('') // Clear URL on error - don't show error toast for this
      } finally {
        setLoadingFile(false)
      }
    }

    fetchFileUrl()
  }, [pieceId])

  // Function to fetch site name when job is selected
  const fetchSiteName = useCallback(async (jobId: number) => {
    if (!jobId || jobId === 0) {
      setFormState(prevState => ({ ...prevState, siteName: '' }))
      return
    }

    try {
      const jobDetails = await getTargetJob(jobId)
      setFormState(prevState => ({
        ...prevState,
        siteName: jobDetails.site?.siteAddr || ''
      }))
    } catch (error) {
      console.error('Error fetching job site details:', error)
      setFormState(prevState => ({ ...prevState, siteName: '' }))
    }
  }, [])

  // Watch for job selection changes and fetch site name
  useEffect(() => {
    if (formJobId && formJobId !== formState.jobId) {
      setFormState(prev => ({ ...prev, jobId: formJobId }))
      fetchSiteName(formJobId)
    }
  }, [formJobId, formState.jobId, fetchSiteName])

  // Navigation functions
  const handlePreviousPiece = useCallback(() => {
    if (props.piecesList && props.currentPieceIndex !== undefined && props.onNavigateToPiece) {
      const prevIndex = props.currentPieceIndex - 1
      if (prevIndex >= 0) {
        const prevPieceId = props.piecesList[prevIndex]
        props.onNavigateToPiece(prevPieceId)
      }
    }
  }, [props.piecesList, props.currentPieceIndex, props.onNavigateToPiece])

  const handleNextPiece = useCallback(() => {
    if (props.piecesList && props.currentPieceIndex !== undefined && props.onNavigateToPiece) {
      const nextIndex = props.currentPieceIndex + 1
      if (nextIndex < props.piecesList.length) {
        const nextPieceId = props.piecesList[nextIndex]
        props.onNavigateToPiece(nextPieceId)
      }
    }
  }, [props.piecesList, props.currentPieceIndex, props.onNavigateToPiece])

  const canNavigatePrevious = useCallback(() => {
    return props.piecesList && props.currentPieceIndex !== undefined && props.currentPieceIndex > 0
  }, [props.piecesList, props.currentPieceIndex])

  const canNavigateNext = useCallback(() => {
    return props.piecesList && props.currentPieceIndex !== undefined && props.currentPieceIndex < props.piecesList.length - 1
  }, [props.piecesList, props.currentPieceIndex])

  const getCurrentPosition = useCallback(() => {
    if (props.piecesList && props.currentPieceIndex !== undefined) {
      return `${props.currentPieceIndex + 1} of ${props.piecesList.length}`
    }
    return ''
  }, [props.piecesList, props.currentPieceIndex])

  const handleSubmit = useCallback(async (formData: any) => {
    try {
      setButtonSpinner(true)
      

      
      console.log('Form data being saved:', formData)
      
      const updateData = {
        piecesId: formState.piecesId,
        jobId: formData.jobId?.value || formState.jobId,
        supplierName: formData.supplierName || formState.supplierName,
        total: formState.total,
        subTotal: formState.subTotal,
        totalTax: formState.totalTax,
        invoiceId: sanitizeInvoiceId(formState.invoiceId),
        invoiceDate: formData.invoiceDate instanceof Date ? toApiDate(formData.invoiceDate) : (formData.invoiceDate || formState.invoiceDate),
        dueDate: formData.dueDate instanceof Date ? toApiDate(formData.dueDate) : (formData.dueDate || formState.dueDate || null),
        worktypeId: formData.worktypeId?.value || formState.worktypeId,
        documentTypeId: formState.documentTypeId,
        description: formData.description || formState.description,
        comments: formData.comments || formState.comments,
        flagged: formState.flagged,
        purchaseOrder: formData.purchaseOrder || formState.purchaseOrder,
        submitToXero: formData.submitToXero !== undefined ? formData.submitToXero : formState.submitToXero,
        xeroTrackingDepartmentId: formData.xeroTrackingDepartmentId?.value || formState.xeroTrackingDepartmentId,
        xeroAccountId: formData.xeroAccountId?.value || formState.xeroAccountId,
        xeroTaxRateId: formData.xeroTaxRateId?.value || formState.xeroTaxRateId,
        xeroTrackingTypeId: formData.xeroTrackingTypeId?.value || formState.xeroTrackingTypeId,
        xeroSupplierId: formData.xeroSupplierId?.value || formState.xeroSupplierId,
      }
      
      console.log('Update data being sent:', updateData)

      await updatePiece(formState.piecesId, updateData)
      showToast('Piece updated successfully', 'success')

      // The purchase order reference may have changed, so re-check the match.
      setPurchaseOrderRefreshToken((prev) => prev + 1)

      // Refresh parent data if callback provided
      if (props.onPieceUpdated) {
        props.onPieceUpdated()
      }
    } catch (error) {
      console.error('Error updating piece:', error)
      showToast('Error updating piece', 'error')
    } finally {
      setButtonSpinner(false)
    }
  }, [formState, props.onPieceUpdated, showToast])

  const handleSendToXero = useCallback(async (formData: any) => {
    try {
      setButtonSpinner(true)
      
      // First, save the piece with current form data
      const updateData = {
        piecesId: formState.piecesId,
        jobId: formData.jobId?.value || formState.jobId,
        supplierName: formData.supplierName || formState.supplierName,
        // Use formState for the money fields (kept in sync by each field's onChange).
        // The visible fields are named total_amount/sub_total/total_tax, so
        // formData.total/subTotal/totalTax come from valueGetter on the stale
        // initialValues copy and would revert edits back to the original values.
        total: formState.total,
        subTotal: formState.subTotal,
        totalTax: formState.totalTax,
        invoiceId: sanitizeInvoiceId(formState.invoiceId),
        invoiceDate: formData.invoiceDate instanceof Date ? toApiDate(formData.invoiceDate) : (formData.invoiceDate || formState.invoiceDate),
        dueDate: formData.dueDate instanceof Date ? toApiDate(formData.dueDate) : (formData.dueDate || formState.dueDate || null),
        worktypeId: formData.worktypeId?.value || formState.worktypeId,
        documentTypeId: formState.documentTypeId,
        description: formData.description || formState.description,
        comments: formData.comments || formState.comments,
        flagged: formState.flagged,
        purchaseOrder: formData.purchaseOrder || formState.purchaseOrder,
        submitToXero: formData.submitToXero !== undefined ? formData.submitToXero : formState.submitToXero,
        xeroTrackingDepartmentId: formData.xeroTrackingDepartmentId?.value || formState.xeroTrackingDepartmentId,
        xeroAccountId: formData.xeroAccountId?.value || formState.xeroAccountId,
        xeroTaxRateId: formData.xeroTaxRateId?.value || formState.xeroTaxRateId,
        xeroTrackingTypeId: formData.xeroTrackingTypeId?.value || formState.xeroTrackingTypeId,
        xeroSupplierId: formData.xeroSupplierId?.value || formState.xeroSupplierId,
      }

      await updatePiece(formState.piecesId, updateData)

      // Validate Xero fields
      const missingFields: string[] = []
      if (!updateData.xeroTrackingDepartmentId || updateData.xeroTrackingDepartmentId === 0) {
        missingFields.push('Xero Department')
      }
      if (!updateData.xeroAccountId || updateData.xeroAccountId === 0) {
        missingFields.push('Xero Account')
      }
      if (!updateData.xeroTaxRateId || updateData.xeroTaxRateId === 0) {
        missingFields.push('Xero Tax Rate')
      }
      if (!updateData.xeroTrackingTypeId || updateData.xeroTrackingTypeId === 0) {
        missingFields.push('Xero Type')
      }
      if (!updateData.xeroSupplierId || updateData.xeroSupplierId === 0) {
        missingFields.push('Xero Supplier')
      }
      
      if (missingFields.length > 0) {
        showToast(`Cannot send to Xero. Please complete the following fields: ${missingFields.join(', ')}`, 'error')
        setButtonSpinner(false)
        return
      }
      
      // All fields are valid, proceed to send to Xero
      await createXeroPurchaseInvoice(formState.piecesId)
      showToast('Successfully created Xero purchase invoice', 'success')
      
      // Refresh the piece data to get updated status
      const updatedPiece = await getPieceById(formState.piecesId)
      setFormState(prev => mapPieceDataToFormState(updatedPiece, prev.siteName))
      
      // Refresh parent data if callback provided
      if (props.onPieceUpdated) {
        props.onPieceUpdated()
      }
    } catch (error) {
      console.error('Error creating Xero purchase invoice:', error)
      showToast('Failed to create Xero purchase invoice', 'error')
    } finally {
      setButtonSpinner(false)
    }
  }, [formState, showToast, props.onPieceUpdated])

  const handleDelete = useCallback(async () => {
    if (window.confirm('Are you sure you want to delete this piece? This action cannot be undone.')) {
      try {
        setButtonSpinner(true)
        await deletePiece(formState.piecesId)
        showToast('Piece deleted successfully', 'success')
        
        // Notify parent to refresh and close the form
        if (props.onPieceUpdated) {
          props.onPieceUpdated()
        }
        if (props.onClose) {
          props.onClose()
        }
      } catch (error) {
        console.error('Error deleting piece:', error)
        showToast('Failed to delete piece', 'error')
      } finally {
        setButtonSpinner(false)
      }
    }
  }, [formState.piecesId, showToast, props])

  // Memoize initial values to prevent unnecessary re-renders
  const initialFormValues = useMemo(() => ({
    ...formState,
    invoiceDate: formState.invoiceDate ? parseDate(formState.invoiceDate) : null,
    dueDate: formState.dueDate ? parseDate(formState.dueDate) : null,
    jobId: jobsData.find(job => job.value === formState.jobId) || null,
    worktypeId: worktypeData.find(worktype => worktype.value === formState.worktypeId) || null,
    xeroTrackingDepartmentId: xeroDepartmentData.find(dept => dept.value === formState.xeroTrackingDepartmentId) || null,
    xeroAccountId: xeroAccountData.find(acc => acc.value === formState.xeroAccountId) || null,
    xeroTaxRateId: xeroTaxRateData.find(tax => tax.value === formState.xeroTaxRateId) || null,
    xeroTrackingTypeId: xeroTypeData.find(type => type.value === formState.xeroTrackingTypeId) || null,
    xeroSupplierId: xeroSupplierData.find(supplier => supplier.value === formState.xeroSupplierId) || null,
  }), [formState, jobsData, worktypeData, xeroDepartmentData, xeroAccountData, xeroTaxRateData, xeroTypeData, xeroSupplierData]);

  console.log('Debug: loading =', loading, 'piecesId =', formState.piecesId, 'jobsData.length =', jobsData.length, 'dropdownsLoaded =', dropdownsLoaded)
  console.log('Debug: xero data lengths - departments:', xeroDepartmentData.length, 'accounts:', xeroAccountData.length, 'taxes:', xeroTaxRateData.length, 'types:', xeroTypeData.length, 'suppliers:', xeroSupplierData.length)

  if (loading || formState.piecesId === 0 || !dropdownsLoaded) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Loading piece data...</div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* PDF Viewer Section */}
      <div style={{ 
        width: '55%', 
        padding: '1rem',
        borderRight: '1px solid #ddd',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Navigation Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          marginBottom: '1rem' 
        }}>
          <h3 style={{ margin: 0, color: 'rgb(36,32,56)' }}>Document</h3>
          {props.piecesList && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'rgb(36,32,56)' }}>
                {getCurrentPosition()}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handlePreviousPiece}
                  disabled={!canNavigatePrevious()}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: canNavigatePrevious() ? '#f8f9fa' : '#e9ecef',
                    cursor: canNavigatePrevious() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Previous piece"
                >
                  <FaChevronLeft style={{ fontSize: '0.8rem' }} />
                </button>
                <button
                  onClick={handleNextPiece}
                  disabled={!canNavigateNext()}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: canNavigateNext() ? '#f8f9fa' : '#e9ecef',
                    cursor: canNavigateNext() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Next piece"
                >
                  <FaChevronRight style={{ fontSize: '0.8rem' }} />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* PDF Viewer */}
        <div style={{ flex: 1 }}>
          {loadingFile ? (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa'
            }}>
              <div>Loading document...</div>
            </div>
          ) : fileUrl ? (
            <PdfViewer documentUrl={fileUrl} width="100%" height="100%" />
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              color: '#6c757d'
            }}>
              <div>No document available</div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Form Section */}
      <div style={{ 
        width: '45%', 
        padding: '1rem',
        overflow: 'auto'
      }}>
        
        <TabStrip
          selected={activeTab}
          onSelect={(e) => setActiveTab(e.selected)}
          style={{ height: '100%' }}
        >
          <TabStripTab title="Details">
            <Form
          key={`piece-form-${formState.piecesId}-${formState.jobId}`}
          initialValues={initialFormValues}
          onSubmit={handleSubmit}
          render={(formRenderProps) => {
            // Watch for job selection changes and update state
            const currentJobId = formRenderProps.valueGetter('jobId')?.value || 0
            
            // Update formJobId if it has changed
            if (currentJobId && currentJobId !== formJobId) {
              setFormJobId(currentJobId)
            }

            return (
            <FormElement style={{ padding: '1rem', width: '100%' }}>
              <fieldset className={'k-form-fieldset'}>
                
                <Field
                  disabled={props.lockEdit}
                  data={jobsData}
                  name={'jobId'}
                  component={FormDropDownListFilter}
                  label={'Project/Job:'}
                  textField="text"
                  dataItemKey="value"
                  defaultItem={jobsData.find(job => job.value === formState.jobId)}
                  onChange={(e) => {
                    const selectedJobId = e.value?.value || 0
                    setFormState({ ...formState, jobId: selectedJobId })
                    if (selectedJobId) {
                      setFormJobId(selectedJobId)
                    }
                  }}
                />

                <Field
                  key={`siteName-${formState.siteName}`}
                  disabled={true}
                  name={'siteName'}
                  component={FormInput}
                  label={'Site:'}
                  defaultValue={formState.siteName}
                  placeholder="Select a job to see site information"
                />

                <Field
                  disabled={props.lockEdit}
                  name={'supplierName'}
                  component={FormInput}
                  label={'Supplier Name:'}
                  defaultValue={formState.supplierName}
                  onChange={(e) => {
                    setFormState({ ...formState, supplierName: e.value })
                  }}
                />

                <Field
                  disabled={props.lockEdit}
                  data={documentTypes}
                  name={'document_type_id'}
                  component={FormDropDownList}
                  label={'Document Type:'}
                  textField="text"
                  dataItemKey="value"
                  defaultItem={documentTypes.find(type => type.value === formState.documentTypeId)}
                  onChange={(e) => {
                    setFormState({ 
                      ...formState, 
                      documentTypeId: e.value?.value || 1
                    })
                  }}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'invoice_id'}
                  component={FormInput}
                  label={'Invoice ID:'}
                  defaultValue={formState.invoiceId}
                  onChange={(e) => {
                    const sanitizedValue = sanitizeInvoiceId(e.value)
                    setFormState({ ...formState, invoiceId: sanitizedValue })
                  }}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'invoiceDate'}
                  component={FormDatePicker}
                  label={'Invoice Date:'}
                  key={`invoiceDate-${formState.piecesId}`}
                  onChange={(e) => {
                    const invoiceDate = e.value
                    setFormState((prev) => ({
                      ...prev,
                      invoiceDate: invoiceDate instanceof Date ? toApiDate(invoiceDate) : '',
                    }))
                    // Fill the due date in from the terms, but never overwrite a
                    // date someone has already set by hand.
                    if (invoiceDate instanceof Date && !formRenderProps.valueGetter('dueDate')) {
                      formRenderProps.onChange('dueDate', { value: calculateDueDate(invoiceDate) })
                    }
                  }}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'dueDate'}
                  component={FormDatePicker}
                  label={'Due Date:'}
                  key={`dueDate-${formState.piecesId}`}
                  onChange={(e) => {
                    setFormState((prev) => ({
                      ...prev,
                      dueDate: e.value instanceof Date ? toApiDate(e.value) : '',
                    }))
                  }}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'sub_total'}
                  component={FormNumericTextBox}
                  label={'Sub Total:'}
                  defaultValue={parseFloat(formState.subTotal) || 0}
                  onChange={(e) => {
                    setFormState({ ...formState, subTotal: e.value?.toString() || '0' })
                  }}
                  format="'£'#,##0.00"
                  key={`subTotal-${formState.piecesId}`}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'total_tax'}
                  component={FormNumericTextBox}
                  label={'Total Tax:'}
                  defaultValue={parseFloat(formState.totalTax) || 0}
                  onChange={(e) => {
                    setFormState({ ...formState, totalTax: e.value?.toString() || '0' })
                  }}
                  format="'£'#,##0.00"
                  key={`totalTax-${formState.piecesId}`}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'total_amount'}
                  component={FormNumericTextBox}
                  label={'Total:'}
                  defaultValue={parseFloat(formState.total) || 0}
                  onChange={(e) => {
                    setFormState({ ...formState, total: e.value?.toString() || '0' })
                  }}
                  format="'£'#,##0.00"
                  key={`total-field-${formState.piecesId}`}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'description'}
                  component={FormInput}
                  label={'Description:'}
                  defaultValue={formState.description}
                  onChange={(e) => {
                    setFormState({ ...formState, description: e.value })
                  }}
                />

                <Field
                  disabled={props.lockEdit}
                  name={'comments'}
                  component={FormInput}
                  label={'Comments:'}
                  defaultValue={formState.comments}
                  onChange={(e) => {
                    setFormState({ ...formState, comments: e.value })
                  }}
                />

                {/* Xero Integration Fields */}
                <Field
                  disabled={props.lockEdit}
                  data={xeroDepartmentData}
                  name={'xeroTrackingDepartmentId'}
                  component={FormDropDownListFilter}
                  label={'Xero Department:'}
                  textField="text"
                  dataItemKey="value"

                />

                <Field
                  disabled={props.lockEdit}
                  data={xeroAccountData}
                  name={'xeroAccountId'}
                  component={FormDropDownListFilter}
                  label={'Xero Account:'}
                  textField="text"
                  dataItemKey="value"

                />

                <Field
                  disabled={props.lockEdit}
                  data={xeroTaxRateData}
                  name={'xeroTaxRateId'}
                  component={FormDropDownListFilter}
                  label={'Xero Tax Rate:'}
                  textField="text"
                  dataItemKey="value"

                />

                <Field
                  disabled={props.lockEdit}
                  data={xeroTypeData}
                  name={'xeroTrackingTypeId'}
                  component={FormDropDownListFilter}
                  label={'Xero Type:'}
                  textField="text"
                  dataItemKey="value"

                />

                <Field
                  disabled={props.lockEdit}
                  data={xeroSupplierData}
                  name={'xeroSupplierId'}
                  component={FormDropDownListFilter}
                  label={'Xero Supplier:'}
                  textField="text"
                  dataItemKey="value"

                />

                {/* Ties this invoice to a purchase order. Suggests a match on
                    the reference above; the user confirms it. */}
                <PiecePurchaseOrderPanel
                  piecesId={formState.piecesId}
                  jobId={formState.jobId}
                  lockEdit={props.lockEdit}
                  refreshToken={purchaseOrderRefreshToken}
                />

                <div style={{ 
                  marginTop: '1rem', 
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Switch
                    checked={formState.flagged}
                    onChange={(e) => setFormState({ ...formState, flagged: e.target.value })}
                    disabled={props.lockEdit}
                  />
                  <label style={{ fontSize: "10pt", color: "rgb(36, 32, 56)" }}>
                    Flag
                  </label>
                </div>

                {!props.lockEdit && (
                  <div className='button__container' style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
                    {buttonSpinner ? (
                      <button disabled={true} className='form__edit__button edit'>
                        Saving...
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          // Get current form values directly from formState and form fields
                          const currentFormData = {
                            jobId: formRenderProps.valueGetter('jobId'),
                            supplierName: formRenderProps.valueGetter('supplierName'),
                            total: formRenderProps.valueGetter('total'),
                            subTotal: formRenderProps.valueGetter('subTotal'),
                            totalTax: formRenderProps.valueGetter('totalTax'),
                            invoiceId: formRenderProps.valueGetter('invoiceId'),
                            invoiceDate: formRenderProps.valueGetter('invoiceDate'),
                            dueDate: formRenderProps.valueGetter('dueDate'),
                            worktypeId: formRenderProps.valueGetter('worktypeId'),
                            documentTypeId: formRenderProps.valueGetter('documentTypeId'),
                            description: formRenderProps.valueGetter('description'),
                            comments: formRenderProps.valueGetter('comments'),
                            purchaseOrder: formRenderProps.valueGetter('purchaseOrder'),
                            submitToXero: formRenderProps.valueGetter('submitToXero'),
                            xeroTrackingDepartmentId: formRenderProps.valueGetter('xeroTrackingDepartmentId'),
                            xeroAccountId: formRenderProps.valueGetter('xeroAccountId'),
                            xeroTaxRateId: formRenderProps.valueGetter('xeroTaxRateId'),
                            xeroTrackingTypeId: formRenderProps.valueGetter('xeroTrackingTypeId'),
                            xeroSupplierId: formRenderProps.valueGetter('xeroSupplierId'),
                          }
                          handleSubmit(currentFormData)
                        }}
                        className='form__edit__button edit'
                        type="button"
                      >
                        Save changes to Piece
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        // Get current form values directly from form fields
                        const currentFormData = {
                          jobId: formRenderProps.valueGetter('jobId'),
                          supplierName: formRenderProps.valueGetter('supplierName'),
                          total: formRenderProps.valueGetter('total'),
                          subTotal: formRenderProps.valueGetter('subTotal'),
                          totalTax: formRenderProps.valueGetter('totalTax'),
                          invoiceId: formRenderProps.valueGetter('invoiceId'),
                          invoiceDate: formRenderProps.valueGetter('invoiceDate'),
                          dueDate: formRenderProps.valueGetter('dueDate'),
                          worktypeId: formRenderProps.valueGetter('worktypeId'),
                          documentTypeId: formRenderProps.valueGetter('documentTypeId'),
                          description: formRenderProps.valueGetter('description'),
                          comments: formRenderProps.valueGetter('comments'),
                          purchaseOrder: formRenderProps.valueGetter('purchaseOrder'),
                          submitToXero: formRenderProps.valueGetter('submitToXero'),
                          xeroTrackingDepartmentId: formRenderProps.valueGetter('xeroTrackingDepartmentId'),
                          xeroAccountId: formRenderProps.valueGetter('xeroAccountId'),
                          xeroTaxRateId: formRenderProps.valueGetter('xeroTaxRateId'),
                          xeroTrackingTypeId: formRenderProps.valueGetter('xeroTrackingTypeId'),
                          xeroSupplierId: formRenderProps.valueGetter('xeroSupplierId'),
                        }
                        handleSendToXero(currentFormData)
                      }}
                      className='form__edit__button'
                      type="button"
                      style={{ 
                        backgroundColor: formState.powerAutomateProcessed === 1 ? '#cccccc' : '#0066cc', 
                        color: 'white',
                        border: `1px solid ${formState.powerAutomateProcessed === 1 ? '#cccccc' : '#0066cc'}`,
                        cursor: formState.powerAutomateProcessed === 1 ? 'not-allowed' : 'pointer'
                      }}
                      disabled={buttonSpinner || formState.powerAutomateProcessed === 1}
                      title={formState.powerAutomateProcessed === 1 ? 'This piece has already been processed by Power Automate and cannot be sent to Xero again' : 'Send this piece to Xero'}
                    >
                      {formState.powerAutomateProcessed === 1 ? 'Already Processed' : 'Send to Xero'}
                    </button>
                    
                    <button
                      onClick={handleDelete}
                      className='form__edit__button'
                      type="button"
                      style={{ 
                        backgroundColor: '#dc3545', 
                        color: 'white',
                        border: '1px solid #dc3545',
                        marginLeft: 'auto'
                      }}
                      disabled={buttonSpinner}
                      title='Delete this piece'
                    >
                      Delete Piece
                    </button>
                  </div>
                )}

              </fieldset>
            </FormElement>
            )
          }}
        />
          </TabStripTab>
          
          <TabStripTab title="Split">
            <PiecesLineitemManager
              piecesId={formState.piecesId}
              xeroAccountData={xeroAccountData}
              xeroSupplierData={xeroSupplierData}
              xeroDepartmentData={xeroDepartmentData}
              xeroTaxRateData={xeroTaxRateData}
              xeroTypeData={xeroTypeData}
              jobsData={jobsData}
              lockEdit={props.lockEdit}
            />
          </TabStripTab>
        </TabStrip>
      </div>
    </div>
  )
}

export default EditPieceForm